/*
  supabaseClient.js — the one place the Supabase connection is created.

  URL and anon key come from the environment (.env locally, Netlify dashboard live).
  If they're missing, `supabase` is null and the app quietly stays on localStorage.
*/
import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Sign-in state lives in localStorage with automatic token refresh: techs open
// the app from a link in a text message, and every such open is a cold start —
// sessionStorage sign-in died on each one and dumped them back at the login
// screen mid-route (Shaun's field test, 08/18). The owner's session-security
// rule is kept by the 88-minute idle sign-out in App, which still fires the
// moment a long-idle phone comes back — this only stops ACTIVE work from
// being interrupted by a reload or a re-opened link.
export const supabase = url && anon ? createClient(url, anon, {
  auth: { autoRefreshToken: true, persistSession: true },
}) : null;

// 'local' (browser only) or 'supabase' (shared). Default local so nothing breaks
// until the table + sign-in are wired and tested.
export const STORAGE_BACKEND = (import.meta.env.VITE_STORAGE_BACKEND || "local").toLowerCase();

// A runtime override (localStorage) lets the owner flip modes without a rebuild,
// and flip back if anything's off — so switching is always reversible.
export function backendMode() {
  try {
    const o = localStorage.getItem("pooldesk:backend");
    if (o === "supabase" || o === "local") return o;
  } catch {}
  return STORAGE_BACKEND;
}
export function setBackendMode(mode) {
  try { localStorage.setItem("pooldesk:backend", mode); } catch {}
}
// True only when we're actually able to run against Supabase.
export const serverMode = () => backendMode() === "supabase" && !!supabase;
